import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Profile from "./Profile";
import Notifications from "./Notifications";
import Statistics from "./Statistics";
import CreateAd from "./CreateAd";
import { CreateAdProvider } from "../../context/CreateAdContext";
import MobileBottomNavbar from "./components/Navigation/MobileBottomNavbar";
import MobileTopNavbar from "./components/Navigation/MobileTopNavbar";
import MobileNavDrawer from "./components/Navigation/MobileNavDrawer";
import DesktopSidebar from "./components/Navigation/DesktopSidebar";
import backgroundImage from "assets/images/backgroundDashboard.png";
import ConnectedPlatforms from "./ConnectedPlatforms";
import AdsRuleSet from "./AdsRuleSet";
import AdsHistory from "./AdsHistory";
import CampaignManager from "./CampaignManager";
import PixelsManager from "./PixelsManager";
import MetaCatalogManager from "./MetaCatalogManager";
import CreateAdFromShopify from "./CreateAdFromShopify";
import Billing from "./Billing";
import { removeAuthToken } from "utils/auth";
import { getRequest } from "services/api/apiService";
import CreateAdWithAI from "./CreateAdWithAI";
import OnboardingFlow from "./OnboardingFlow";
import StoreManager from "./StoreManager";
import TrackingDashboard from "./TrackingDashboard";
import TrackingSetup from "./TrackingSetup";
import CreativeStudio from "./CreativeStudio";
import StrategyLauncher from "./StrategyLauncher";
import Credits from "./Credits";
import CinemaStudio from "../Studio/CinemaStudio";
import CreativeLibrary from "./CreativeLibrary";
import { AdsJourney, AbTestingJourney, ShopifyJourney, TrackingJourney, CinemaStudioJourney } from "../Journey/JourneyPages";
import Experiments from "./Experiments";
import SkillsManager from "./SkillsManager";
import GuidedTour, { TOUR_KEY } from "./GuidedTour";
import AiChatBot from "../AiChatBot/AiChatBot";
import AlexChatPage from "./AlexChatPage";
import ChatHistoryPage from "./ChatHistoryPage";
import AccountsWarningBanner from "./AccountsWarningBanner";

const ONBOARDING_KEY = "onebotads_onboarding_done";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState({ name: "", email: "", isPremium: false });
  const [unreadCount, setUnreadCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [journey, setJourney] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Visite guidée spotlight (première connexion : Overview → fonctionnalités
  // → Plateformes connectées). Relançable via le bouton flottant "?".
  const [showTour, setShowTour] = useState(false);

  // Clé de dismissal par parcours : le parcours Shopify a son propre flag,
  // pour qu'un marchand voie son onboarding même si le générique a été passé.
  const onboardingKeyFor = (j) => (j && j.key !== "default" ? `${ONBOARDING_KEY}_${j.key}` : ONBOARDING_KEY);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await getRequest("/api/auth/user-info");
        setUser(response);
        // Langue du profil (choisie à l'inscription ou via 🌐) : appliquée si
        // l'utilisateur n'a pas déjà un choix local sur CE navigateur.
        if (response?.language && !localStorage.getItem("oba_lang")) {
          localStorage.setItem("oba_lang", response.language);
          window.dispatchEvent(new Event("oba-lang-sync"));
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    };

    const fetchUnreadCount = async () => {
      try {
        const res = await getRequest("/api/notifications/unread-count");
        setUnreadCount(res.count || 0);
      } catch {}
    };

    fetchUserDetails();
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);

    // Parcours adapté : ?source=shopify (persisté) ou boutique déjà connectée
    // → parcours marchand Shopify ; sinon parcours générique.
    const urlSource = new URLSearchParams(window.location.search).get("source");
    if (urlSource) localStorage.setItem("oba_source", urlSource);
    const source = urlSource || localStorage.getItem("oba_source") || "";
    getRequest("/api/onboarding/journey", source ? { source } : {})
      .then((j) => {
        setJourney(j);
        // Première connexion = rien de relié : l'utilisateur est routé vers
        // Plateformes connectées (voir Login.jsx) — on ne lance PAS la visite
        // guidée pour ne pas l'éloigner de la connexion de son premier compte.
        // Elle se lancera à la session suivante, sur l'Overview.
        const firstConnection = (j?.platformsConnected || 0) === 0 && !j?.shopifyConnected;
        if (!localStorage.getItem(onboardingKeyFor(j))) {
          setShowOnboarding(true);
        } else if (!firstConnection && !localStorage.getItem(TOUR_KEY)) {
          // Onboarding déjà vu mais visite guidée jamais faite → la lancer.
          setShowTour(true);
        }
      })
      .catch(() => {
        if (!localStorage.getItem(ONBOARDING_KEY)) setShowOnboarding(true);
        else if (!localStorage.getItem(TOUR_KEY)) setShowTour(true);
      });

    return () => clearInterval(interval);
  }, []);

  const handleDismissOnboarding = () => {
    localStorage.setItem(onboardingKeyFor(journey), "true");
    setShowOnboarding(false);
    // Enchaîne le déroulé : modal de bienvenue → visite guidée de l'interface.
    // Sauf en toute première connexion (rien de relié) : on laisse
    // l'utilisateur sur Plateformes connectées brancher son premier compte —
    // la visite se lancera à la session suivante sur l'Overview.
    const firstConnection = (journey?.platformsConnected || 0) === 0 && !journey?.shopifyConnected;
    if (!firstConnection && !localStorage.getItem(TOUR_KEY)) setShowTour(true);
  };

  const handleLogout = () => {
    removeAuthToken();
    navigate("/");
  };

  const activeStyle =
    "text-white bg-[#221C51]/90 rounded-lg shadow-md px-4 py-2";
  const inactiveStyle =
    "text-[#221C51] bg-transparent hover:bg-[#F6F6F6] hover:text-[#221C51] transition ease-in-out duration-200 rounded-lg px-4 py-2 transform hover:scale-105";

  // Pages qui montent leur PROPRE instance d'Alex (panneau intégré AI Launcher,
  // bulle Shopify Ads) — on y masque la bulle globale pour éviter le doublon.
  const hasOwnAlex = location.pathname.includes("create-ad-ai") || location.pathname.includes("shopify-ads") || location.pathname.startsWith("/dashboard/alex");

  return (
    // Provider au niveau Dashboard : partagé entre la bulle Alex GLOBALE
    // (disponible sur toutes les pages) et le formulaire classique CreateAd —
    // quand Alex remplit des champs depuis n'importe quelle page, applyActions
    // navigue vers /dashboard/create-ad et le formulaire se remplit.
    <CreateAdProvider>
    <div
      className="min-h-screen h-screen bg-cover bg-center p-4 flex flex-col md:flex-row overflow-hidden"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Sidebar for Web */}
      <DesktopSidebar
        user={user}
        activeStyle={activeStyle}
        inactiveStyle={inactiveStyle}
        handleLogout={handleLogout}
        unreadCount={unreadCount}
        onOpenOnboarding={() => setShowOnboarding(true)}
        persona={journey?.key === "shopify" || journey?.shopifyConnected ? "shopify" : "default"}
      />

      {/* Navbar and User Section for Mobile */}
      <MobileTopNavbar
        user={user}
        onOpenMenu={() => setMobileMenuOpen(true)}
      />
      <MobileNavDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        handleLogout={handleLogout}
        unreadCount={unreadCount}
        persona={journey?.key === "shopify" || journey?.shopifyConnected ? "shopify" : "default"}
      />

      {/* Main Content */}
      <div className="flex-1 w-full bg-white rounded-xl pt-2 p-6 shadow-md overflow-y-auto mb-20 mt-[-20px] md:mt-[-10px] md:mb-0 md:ml-6">
        {/* Avertissement : plateforme connectée sans compte pub sélectionné */}
        <AccountsWarningBanner />
        <Routes>
          <Route index element={<Navigate to="statistics" replace />} />
          {/* Provider hérité du niveau Dashboard (voir plus bas) — partagé avec
              la bulle Alex globale pour que ses actions remplissent CE formulaire. */}
          <Route path="create-ad" element={<CreateAd isPremium={user.isPremium} />} />
          <Route path="create-ad-ai" element={<CreateAdWithAI isPremium={user.isPremium} />} />
          <Route path="profile" element={<Profile />} />
          <Route path="notifications" element={<Notifications />} />
          {/* Ad Manager (history) retiré — redirige vers Campagnes */}
          <Route path="history" element={<Navigate to="/dashboard/campaigns" replace />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="connected-platforms" element={<ConnectedPlatforms />} />
          <Route path="skills" element={<SkillsManager />} />
          <Route path="alex" element={<AlexChatPage />} />
          <Route path="chat-history" element={<ChatHistoryPage />} />
          <Route path="stores" element={<StoreManager />} />
          <Route path="tracking" element={<TrackingDashboard />} />
          <Route path="tracking-setup" element={<TrackingSetup />} />
          <Route path="creative-studio" element={<CreativeStudio />} />
          <Route path="strategy" element={<StrategyLauncher />} />
          <Route path="credits" element={<Credits />} />
          <Route path="cinema-studio/:tool?" element={<CinemaStudio />} />
          <Route path="creative-library" element={<CreativeLibrary />} />
          <Route path="journey/cinema" element={<CinemaStudioJourney />} />
          <Route path="journey/ads" element={<AdsJourney />} />
          <Route path="journey/ab-testing" element={<AbTestingJourney />} />
          <Route path="journey/shopify" element={<ShopifyJourney />} />
          <Route path="journey/tracking" element={<TrackingJourney />} />
          <Route path="experiments" element={<Experiments />} />
          <Route path="ads-ruleset" element={<AdsRuleSet />} />
          <Route path="ads-history" element={<AdsHistory />} />
          <Route path="campaigns" element={<CampaignManager />} />
          <Route path="pixels" element={<PixelsManager />} />
          <Route path="meta-catalog" element={<MetaCatalogManager />} />
          <Route path="shopify-ads" element={<CreateAdFromShopify isPremium={user.isPremium} />} />
          <Route path="billing" element={<Billing />} />
        </Routes>
      </div>

      {/* Bottom Navigation Bar for Mobile */}
      <MobileBottomNavbar onOpenMenu={() => setMobileMenuOpen(true)} />

      {/* Onboarding overlay — parcours adapté (générique ou marchand Shopify) */}
      {showOnboarding && <OnboardingFlow onDismiss={handleDismissOnboarding} journey={journey} />}
      {showTour && !showOnboarding && (
        <GuidedTour
          onClose={() => setShowTour(false)}
          variant={journey?.key === "shopify" ? "shopify" : "default"}
        />
      )}

      {/* Bouton flottant pour (re)lancer la visite guidée — bas-gauche pour ne
          pas gêner la bulle du chatbot Alex (bas-droite). */}
      {!showTour && !showOnboarding && (
        <button
          onClick={() => setShowTour(true)}
          title="Relancer la visite guidée"
          className="fixed bottom-20 md:bottom-5 left-4 z-40 w-9 h-9 rounded-full bg-white border border-gray-200 shadow-lg text-[#221C51] font-black text-sm hover:bg-gray-50 hover:scale-110 transition"
        >
          ?
        </button>
      )}

      {/* Alex partout : bulle flottante globale (bas-droite), sauf sur les
          pages qui embarquent déjà leur propre chat. Montée HORS des Routes,
          elle survit à la navigation — la conversation n'est pas perdue quand
          Alex redirige vers le formulaire. */}
      {!hasOwnAlex && <AiChatBot />}
    </div>
    </CreateAdProvider>
  );
};

export default Dashboard;
